<template>
  <v-col class="just">
    <v-form
      ref="form-pictures"
      v-model="isFormValid"
    >
      <v-row class="justify-center py-10 gap-4">
        <avs-button
          color="blue white--text"
          iconI="mdi-image-plus"
          @click="handleUploadInspectionPicture"
          :disabled="computedDisabled"
          :spantext="
            $t('actions.add_from_inspection', {
              entity: $tc('commons.picture', 1).toLowerCase()
            })
          "
          ><template v-slot:name>{{
            $t("actions.pick", {
              entity: $tc("commons.picture", 1).toLowerCase()
            })
          }}</template>
        </avs-button>

        <avs-button
          color="blue white--text"
          iconI="mdi-upload"
          @click="handleUpload"
          :disabled="computedDisabled"
          :spantext="$t('actions.upload', { entity: $tc('commons.picture', 1).toLowerCase() })"
          ><template v-slot:name>{{
            $t("actions.upload", { entity: $tc("commons.picture", 1).toLowerCase() })
          }}</template>
        </avs-button>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          sm="6"
          md="3"
          v-for="(f, i) in pictures.filter(p => !p.removed)"
          :key="i"
        >
          <v-card class="mx-auto overflow-hidden">
            <div class="hover-effect">
              <v-fade-transition>
                <v-overlay
                  class="overlay"
                  absolute
                  color="primary"
                  z-index="99"
                >
                  <PicturePreview v-model="f.file_url">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        text
                        v-on="on"
                        :attrs="attrs"
                        class="center-button"
                      >
                        <v-icon left>mdi-eye</v-icon>
                        {{ $t("commons.preview") }}
                      </v-btn>
                    </template>
                  </PicturePreview>
                </v-overlay>
              </v-fade-transition>
              <v-img
                :src="f.file_url"
                height="150"
              ></v-img>
            </div>

            <v-card-title class="py-0">
              <v-text-field
                :rules="rules.name"
                v-model="f.name"
                :label="$t('commons.name')"
                @input="$emit('input', pictures)"
                :disabled="computedDisabled"
              ></v-text-field>
            </v-card-title>
            <v-card-actions class="pt-0">
              <PictureEditor
                v-model="f.file_url"
                :value="f.gridApplied ? f.file_url_with_grid : f.file_url"
                :key="`picture-editor-${f.timestamp}`"
                @input="$emit('input', pictures)"
                :disabled="computedDisabled"
              >
                <template v-slot:activator="{ on, attrs }">
                  <avs-editButton
                    v-on="on"
                    :attrs="attrs"
                    :disabled="computedDisabled"
                    :fab="!$vuetify.breakpoint.lgAndUp"
                    :spantext="
                      $t('actions.edit', { entity: $tc('commons.picture', 1).toLowerCase() })
                    "
                  />
                </template>
              </PictureEditor>
              <v-spacer></v-spacer>
              <avs-gridButton
                :disabled="computedDisabled"
                @click.stop="applyGrid(f)"
                :spantext="$t('actions.grid', { entity: $tc('commons.picture', 1).toLowerCase() })"
              />
              <v-spacer></v-spacer>
              <avs-deleteButton
                @click.stop="handleOpenDeleteDialog(f, i)"
                :disabled="computedDisabled"
                :spantext="
                  $t('actions.delete', { entity: $tc('commons.picture', 1).toLowerCase() })
                "
              />
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-form>
    <v-dialog
      v-model="dialog"
      max-width="60%"
    >
      <avs-card subtitle>
        <template v-slot:title>
          <v-card-title class="pa-0">
            {{ $t("categories.inspection.screenshots") }}
          </v-card-title>
        </template>
        <template v-slot:subtitle>
          <v-card-subtitle class="pl-0">{{
            $t("messages.information.click_to_add")
          }}</v-card-subtitle>
        </template>
        <template v-slot:content>
          <v-container>
            <v-item-group
              v-model="selectedPictures"
              multiple
            >
              <v-row
                class="justify-center"
                v-if="unassignedPictures.length > 0"
              >
                <v-col
                  cols="12"
                  sm="6"
                  md="3"
                  v-for="(u, i) in unassignedPictures"
                  :key="i"
                >
                  <v-item v-slot="{ active, toggle }">
                    <v-img
                      :src="u.file_url"
                      @click="toggle"
                    >
                      <v-btn icon>
                        <v-icon>
                          {{ active ? "mdi-check-circle" : "mdi-check-circle-outline" }}
                        </v-icon>
                      </v-btn>
                    </v-img>
                  </v-item>
                </v-col>
              </v-row>
              <v-card-text v-else>{{
                $t("messages.information.no_picture_available")
              }}</v-card-text>
            </v-item-group>
          </v-container>
          <v-card-actions class="d-flex justify-end">
            <avs-validateButton
              class="mr-2"
              @click="validate"
              :disabled="selectedPictures.length == 0"
            />

            <avs-cancelButton @click="dialog = false" />
          </v-card-actions>
        </template>
      </avs-card>
    </v-dialog>

    <input
      v-show="false"
      type="file"
      multiple
      accept="image/*"
      ref="uploadInput"
      @change="handleChange"
    />
    <avs-deleteDialog
      v-model="deleteDialogIsOpen"
      @cancel="handleCancel"
      @delete="handleDelete"
      resource="nonconformity picture"
    />
  </v-col>
</template>

<script>
import { imageValidator, required } from "@/utils/validator"
import InspectionService from "@/services/InspectionService"
import _ from "lodash"
export default {
  name: "NonConformityPictures",
  components: {
    PictureEditor: () => import("./NonConformityPictureEditor.vue"),
    PicturePreview: () => import("./NonConformityPicturePreview.vue")
  },

  props: {
    validateCounter: {
      type: Number,
      default: 0
    },
    value: {
      type: Array,
      default: () => []
    },
    inspection: {
      type: Object,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    isDisable: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    computedDisabled() {
      return this.disabled || this.isDisable
    }
  },
  data() {
    return {
      rules: {
        name: [required]
      },
      isFormValid: false,
      pictures: [],
      dialog: false,
      unassignedPictures: [],
      selectedPictures: [],
      deleteDialogIsOpen: false,
      selectedItem: {}
    }
  },
  mounted() {
    if (this.inspection) {
      this.refreshPictures()
    }
    this.setPictures(this.value)
  },

  methods: {
    handleDelete() {
      let f = this.selectedItem.item
      let i = this.selectedItem.indice
      if (f.id && f.unassigned === true) {
        this.pictures.splice(i, 1)
      }
      if (f.id && f.unassigned === undefined) {
        f.removed = true
      } else this.pictures = this.pictures.filter(p => p !== f)

      this.$store.commit("app/message", {
        msg: this.$t("messages.information.nc_pic_removed_step"),
        color: "success"
      })

      this.$emit("input", this.pictures)
      this.deleteDialogIsOpen = false
    },
    handleOpenDeleteDialog(f, i) {
      this.deleteDialogIsOpen = true
      this.selectedItem = { item: f, indice: i }
    },
    handleCancel() {
      this.deleteDialogIsOpen = false
    },
    handleUpload() {
      this.$refs.uploadInput.click()
    },
    handleChange(e) {
      const $ = this
      e.target.files.forEach((picture, i) => {
        if (imageValidator(picture)) {
          let reader = new FileReader()

          reader.addEventListener(
            "load",
            () => {
              $.pictures.push({
                file_url: reader.result,
                label: "",
                hover: false,
                removed: false,
                timestamp: Date.now()
              })
              $.$nextTick(() => {
                $.$emit("input", this.pictures)
              })
            },
            false
          )
          reader.readAsDataURL(picture)
        }
      })
    },
    setPictures(values) {
      this.pictures = values.map(p => ({ removed: false, ...p }))
    },
    refreshPictures() {
      InspectionService.unassignedPictures.getAll(this.inspection.id).then(r => {
        this.unassignedPictures = r.results
      })
    },
    handleUploadInspectionPicture() {
      this.dialog = true
    },
    validate() {
      // list of id of picture already selected
      let nonConfPicID = _.map(this.pictures, "id")
      this.unassignedPictures.forEach((pic, i) => {
        if (this.selectedPictures.includes(i) && !_.includes(nonConfPicID, pic.id)) {
          this.pictures.push({ idd: pic.id, unassigned: true, ...pic })
          this.unassignedPictures = this.unassignedPictures.filter(p => p !== pic)
        }
      })
      this.$emit("input", this.pictures)
      this.dialog = false
    },
    applyGrid(image) {
      // Vérifiez si la grille est déjà appliquée
      if (image.gridApplied) {
        // Si la grille est appliquée, revenez à l'image originale
        image.file_url = image.originalUrl
        image.gridApplied = false
      } else {
        // Si la grille n'est pas appliquée, stockez l'URL originale et appliquez la grille
        if (!image.originalUrl) {
          image.originalUrl = image.file_url // Stockez l'URL de l'image originale
        }

        // Créez un élément canvas
        const canvas = document.createElement("canvas")
        const ctx = canvas.getContext("2d")

        // Chargez l'image
        const img = new Image()
        img.crossOrigin = "anonymous"
        img.src = image.originalUrl
        img.onload = () => {
          // Définissez les dimensions du canvas en fonction de l'image
          canvas.width = img.width
          canvas.height = img.height

          // Dessinez l'image sur le canvas
          ctx.drawImage(img, 0, 0)

          // Dessinez la grille
          const cellSize = 50 // Taille de chaque cellule de la grille en pixels
          const divisionsX = Math.floor(canvas.width / cellSize)
          const divisionsY = Math.floor(canvas.height / cellSize)
          const stepX = canvas.width / divisionsX
          const stepY = canvas.height / divisionsY
          ctx.beginPath()
          for (let i = 0; i <= divisionsX; i++) {
            const x = i * stepX
            const letter = String.fromCharCode("A".charCodeAt(0) + i)
            ctx.moveTo(x, 0)
            ctx.lineTo(x, canvas.height)
            ctx.fillText(letter, x + 2, canvas.height - 5) // Positionner les lettres en bas
          }
          for (let i = 0; i <= divisionsY; i++) {
            const y = i * stepY
            ctx.moveTo(0, y)
            ctx.lineTo(canvas.width, y)
            ctx.fillText(i + 1, 5, canvas.height - y - 2) // Positionner les chiffres en bas
          }
          ctx.strokeStyle = "#000" // Couleur des lignes de la grille
          ctx.stroke()

          // Mettez à jour l'URL de l'image avec le résultat du canvas
          // image.file_url = canvas.toDataURL("image/png")
          image.file_url = canvas.toDataURL()
          image.file_url_with_grid = image.file_url
          image.gridApplied = true

          image.timestamp = Date.now()
          // Émettez l'événement pour mettre à jour les données de l'image
          this.$emit("input", this.pictures)
        }
      }
    }
  },
  watch: {
    validateCounter(newValue) {
      this.$refs["form-pictures"].validate()
    },
    isFormValid(newValue) {
      this.$emit("validated", newValue)
    },
    value: {
      handler(newValue, oldValue) {
        if (newValue) this.setPictures(newValue)
      }
    },
    inspection: function (newValue, oldValue) {
      if (newValue && newValue !== oldValue) {
        this.refreshPictures()
      }
    },
    deep: true
  }
}
</script>

<style scoped>
.hover-effect {
  position: relative;
}
.hover-effect:hover .overlay {
  display: flex;
  align-items: center;
  justify-content: center;
}
.overlay {
  display: none;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
}
.center-button {
  margin: auto;
}
</style>
